/* eslint-disable max-lines */
import React, { useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DatePickerPopover, Dialog, FCWithFragments, MaybeTooltip } from '@modernloop/shared/components';
import { assertIsoTimestamp, getLocalTimezone } from '@modernloop/shared/datetime';
import { PublicError } from '@modernloop/shared/helper-components';
import { DateIcon } from '@modernloop/shared/icons';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { parseISO } from 'date-fns';
import { format as formatTz } from 'date-fns-tz';
import { useSnackbar } from 'notistack';

import {
  EmployeeFragment,
  RescheduleLogModal_RescheduleLogFragment,
  useEmployeeByIdsQuery,
  useRescheduleLogCreateMutation,
  useRescheduleLogUpdateMutation,
  useRescheduleReasonsListQuery,
} from 'src/generated/mloop-graphql';

import {
  selectButtonLabelSxProps_DO_NOT_USE,
  selectButtonProps_DO_NOT_USE,
} from 'src/constants/selectButtonProps_DO_NOT_USE';

import SingleEmployeeSelect from 'src/entities/Select/EmployeeSelect/SingleEmployeeSelect';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useEmployeeId from 'src/hooks/useEmployeeId';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { PAGE_SIZE } from 'src/constants';

interface Props {
  taskId: string;
  onClose: () => void;
}

interface Fragments {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  rescheduleLog?: RescheduleLogModal_RescheduleLogFragment;
}

const RescheduleLogModal: FCWithFragments<Fragments, Props> = ({ rescheduleLog, taskId, onClose }) => {
  const { t } = useTranslation();
  const DEFAULT_REASON = t('hiring-dashboard-tasks:tasks.rescheduleLog.addAReason');
  const { enqueueSnackbar } = useSnackbar();
  const [selectedRescheduleReasonId, setSelectedRescheduleReasonId] = useState(
    rescheduleLog?.rescheduleReasonId || DEFAULT_REASON
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const empId = useEmployeeId();
  const { data, loading } = useEmployeeByIdsQuery({ variables: { input: empId ? [empId] : [] } });
  const [performedBy, setPerformedBy] = useState<EmployeeFragment | undefined>(
    rescheduleLog?.performedBy as EmployeeFragment
  );
  const [rescheduledAt, setRescheduledAt] = useState(
    rescheduleLog ? new Date(rescheduleLog.rescheduledAt) : new Date()
  );
  const [note, setNote] = useState(rescheduleLog?.note || '');
  const [noteError, setNoteError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const [rescheduleLogCreateMutation, { loading: creating }] = useRescheduleLogCreateMutation();
  const [rescheduleLogUpdateMutation, { loading: updating }] = useRescheduleLogUpdateMutation();
  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();
  const { data: rescheduleReasonsData, error: rescheduleReasonsDataError } = useRescheduleReasonsListQuery({
    variables: {
      input: {
        pageInput: {
          limit: PAGE_SIZE,
        },
        prefix: '',
        includeArchived: false,
      },
    },
  });

  if (rescheduleReasonsDataError) {
    enqueueSnackbar(getExternalErrorMessage(rescheduleReasonsDataError), { variant: 'error' });
  }

  const validate = () => {
    const isValid = selectedRescheduleReasonId && selectedRescheduleReasonId !== DEFAULT_REASON && performedBy?.id;
    setShowValidationErrors(!isValid);
    return isValid;
  };

  const createRescheduleLog = async () => {
    await rescheduleLogCreateMutation({
      variables: {
        input: {
          taskId,
          rescheduleReasonId: selectedRescheduleReasonId,
          note,
          performedByEmployeeId: performedBy?.id,
          rescheduledAt,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        refetchScheduleTaskQuery();
        refetchActivityFeed();
        enqueueSnackbar(t('hiring-dashboard-tasks:tasks.rescheduleLog.createdSuccessfully'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      });
  };

  const updateRescheduleLog = async () => {
    await rescheduleLogUpdateMutation({
      variables: {
        input: {
          id: rescheduleLog?.id,
          rescheduleReasonId: selectedRescheduleReasonId,
          note,
          performedByEmployeeId: performedBy?.id,
          rescheduledAt,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        refetchScheduleTaskQuery();
        refetchActivityFeed();
        enqueueSnackbar(t('hiring-dashboard-tasks:tasks.rescheduleLog.updatedSuccessfully'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      });
  };

  // eslint-disable-next-line modernloop/validate-component-definition.cjs
  function getEmployeeLabel() {
    let user = performedBy;
    if (!performedBy) {
      user = data?.employeeByIds?.[0];
      setPerformedBy(user as EmployeeFragment);
    }

    const label = (
      <Stack direction="row" spacing={1} sx={selectButtonLabelSxProps_DO_NOT_USE}>
        <Box ml={-0.5}>
          <Avatar alt={user?.fullName ?? ''} src={user?.internalProfilePictureUrl ?? undefined} />
        </Box>

        <Box
          sx={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <MaybeTooltip label={user?.fullName ?? ''} title={user?.fullName ?? ''} />
        </Box>
      </Stack>
    );

    return (
      <Box width="100%">
        <Button {...selectButtonProps_DO_NOT_USE} sx={selectButtonProps_DO_NOT_USE.sx}>
          {label}
        </Button>
      </Box>
    );
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('hiring-dashboard-tasks:tasks.rescheduleLog.title')}
      subTitle=""
      cancelOptions={{
        onClick: onClose,
        label: t('hiring-dashboard-common:common.cancel'),
      }}
      submitOptions={{
        onClick: async () => {
          if (!validate()) {
            return;
          }
          if (!rescheduleLog) {
            await createRescheduleLog();
          } else {
            await updateRescheduleLog();
          }
          onClose();
        },
        isLoading: creating || updating,
        isDisabled: creating || updating,
        label: t('hiring-dashboard-common:common.save'),
      }}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle2">{t('hiring-dashboard-tasks:tasks.rescheduleLog.rescheduleDate')}</Typography>
          <Button
            sx={{
              alignItems: 'left',
              justifyContent: 'left',
              fontWeight: '200',
              borderRadius: '6px',
            }}
            startIcon={<DateIcon />}
            ref={dateButtonRef}
            onClick={() => setShowDatePicker(true)}
          >
            {formatTz(new Date(rescheduledAt), 'MMM d, yyyy', { timeZone: getLocalTimezone() })}
          </Button>

          <DatePickerPopover
            open={showDatePicker}
            timezone={getLocalTimezone()}
            data-testid="start-date-calendar"
            anchorEl={dateButtonRef.current}
            date={assertIsoTimestamp(rescheduledAt.toISOString())}
            onClose={() => setShowDatePicker(!showDatePicker)}
            onChange={(val) => {
              const currentTime = new Date();

              const newDate = parseISO(val);
              newDate.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0);
              setRescheduledAt(newDate);
            }}
            disablePast={false}
            disableFuture
          />
        </Stack>
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle2">{t('hiring-dashboard-tasks:tasks.rescheduleLog.performedBy')}</Typography>
          <SingleEmployeeSelect
            onEmployeeSelect={(newMember) => setPerformedBy(newMember)}
            getLabel={() => {
              return getEmployeeLabel();
            }}
          />
          {showValidationErrors && !performedBy && (
            <Typography color="error.main" variant="body2">
              {t('hiring-dashboard-tasks:tasks.rescheduleLog.selectEmployee')}
            </Typography>
          )}
        </Stack>
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle2">
            {t('hiring-dashboard-tasks:tasks.rescheduleLog.rescheduleReason')}
          </Typography>
          <Select
            fullWidth
            error={showValidationErrors && selectedRescheduleReasonId === DEFAULT_REASON}
            value={selectedRescheduleReasonId}
            onChange={(val) => {
              setSelectedRescheduleReasonId(val.target.value);
            }}
            renderValue={(value) => {
              const rescheduleReason = rescheduleReasonsData?.rescheduleReasons?.items?.find(
                (item) => item.id === value
              );
              if (rescheduleReason) {
                return <Typography variant="body1">{rescheduleReason.reason}</Typography>;
              }
              return (
                <Typography variant="body1" color="text.secondary">
                  {t('hiring-dashboard-tasks:tasks.rescheduleLog.addAReason')}
                </Typography>
              );
            }}
          >
            {rescheduleReasonsData?.rescheduleReasons?.items?.map((rescheduleReason) => {
              return (
                <MenuItem key={rescheduleReason.id} value={rescheduleReason.id}>
                  <ListItemText primary={rescheduleReason.reason} />
                </MenuItem>
              );
            })}
          </Select>
          {showValidationErrors && selectedRescheduleReasonId === DEFAULT_REASON && (
            <Typography color="error.main" variant="body2">
              {t('hiring-dashboard-tasks:tasks.rescheduleLog.selectRescheduleReason')}
            </Typography>
          )}
        </Stack>
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle2">{t('hiring-dashboard-tasks:tasks.rescheduleLog.note')}</Typography>
          <TextField
            placeholder={t('hiring-dashboard-tasks:tasks.rescheduleLog.notePlaceholder')}
            onChange={(val) => {
              setNote(val.target.value);
              setNoteError('');
            }}
            value={note}
            fullWidth
            multiline
            rows={3}
            error={noteError !== ''}
          />
          {noteError !== '' && <PublicError error={noteError} />}
        </Stack>
      </Stack>
    </Dialog>
  );
};

RescheduleLogModal.fragments = {
  rescheduleLog: gql`
    fragment RescheduleLogModal_rescheduleLog on RescheduleLog {
      id
      note
      rescheduledAt
      createdAt
      updatedAt
      lastUpdatedBy {
        id
        fullName
      }
      rescheduleReasonId
      rescheduleReason {
        reason
      }
      performedBy {
        id
        fullName
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RescheduleLogCreateMutation = gql`
  mutation RescheduleLogCreate($input: RescheduleLogCreateInput!) {
    rescheduleLogCreate(input: $input) {
      rescheduleLog {
        ...RescheduleLogModal_rescheduleLog
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RescheduleLogUpdateMutation = gql`
  mutation RescheduleLogUpdate($input: RescheduleLogUpdateInput!) {
    rescheduleLogUpdate(input: $input) {
      rescheduleLog {
        ...RescheduleLogModal_rescheduleLog
      }
    }
  }
`;

export default RescheduleLogModal;
